<!--
 * @Descripttion: 详情内容展示组件（无弹窗）
 * @version:
 * @Author: hongye
 * @Date: 2026-03-25
 * @LastEditors: hongye
 * @LastEditTime: 2026-03-26 14:06:33
-->
<template>
  <div class="jt-detail-content">
    <Spin :spinning="loading">
      <Form layout="vertical">
        <div v-for="(item, index) in columns" :key="index">
          <div v-if="formatterShow(item.show) !== false" class="detail-item">
            <FormGroup :title="$lang(item.groupTitle)" v-if="item.group" />
            <Row :gutter="24" v-if="!item.slot">
              <Col v-bind="_.span ? { span: _.span } : DEFAULT_SPAN"
                v-for="(_ , idx) in item.childs" :key="idx" v-show="formatterShow(_.show) !== false">

                <FormItem :label="$lang(_.label)">

                  <div class="photo-list" v-if="_.type === 'image'">
                    <div v-if="Array.isArray(_.prop) && _.prop.length > 1" class="photo-list-item">
                      <template v-if="getNestedFieldValue(_.prop)">
                        <template v-if="Array.isArray(getNestedFieldValue(_.prop))">
                          <Image v-for="(img, i) in getNestedFieldValue(_.prop)" :key="i" :src="img.url || img" />
                        </template>
                        <Image v-else :src="getNestedFieldValue(_.prop)" />
                      </template>
                      <div v-else>--</div>
                    </div>

                    <div v-else class="photo-list-item">
                      <template v-if="getFieldValue(_.prop)">
                        <template v-if="Array.isArray(getFieldValue(_.prop))">
                          <Image v-for="(img, i) in getFieldValue(_.prop)" :key="i" :src="img.url || img" />
                        </template>
                        <Image v-else :src="getFieldValue(_.prop)" />
                      </template>
                      <div v-else>--</div>
                    </div>
                  </div>

                  <div v-else>
                    <component v-if="_.customRender" :is="customRender(_.customRender, Array.isArray(_.prop) ? _.prop[_.prop.length - 1] : _.prop)" />
                    <div v-else>
                      <div v-if="_.formatter">{{ formatter(_, Array.isArray(_.prop) ? _.prop[_.prop.length - 1] : _.prop) }}</div>
                      <div v-else>{{ getFieldValue(_.prop) || '--' }}</div>
                    </div>
                  </div>
                </FormItem>
              </Col>
            </Row>
            <slot v-if="item.slot" :name="item.slot"></slot>
            <slot name="afterSlot"></slot>
          </div>
        </div>
      </Form>
    </Spin>
    <slot name="footer"></slot>
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef } from 'vue'
import { Spin, Row, Col, Form, FormItem, Image } from 'ant-design-vue'
import FormGroup from './FormGroup.vue'
import { isSuccess } from '@/utils'

interface IColumnProp {
  label: string
  prop: string | string[]
  span?: number
  show?: boolean | ((data: Record<string, any>) => boolean)
  type?: 'image' | 'text' | 'custom'
  formatter?: (data: Record<string, any>, value: any) => string
  customRender?: (data: Record<string, any>, value: any) => any

}

interface IColumnGroup {
  group: boolean
  groupTitle: string
  childs: IColumnProp[]
  slot?: boolean | string
  show?: boolean | ((data: Record<string, any>) => boolean)
}

interface Props {
  columns: IColumnGroup[]
  data?: Record<string, any>
  loading?: boolean
  params?: Record<string, any>
  ajaxUrl?: (params: Record<string, any>) => Promise<any>
  responseHandle?: (res: any) => void
}

const props = withDefaults(defineProps<Props>(), {
  params: () => ({}),
  data: () => ({}),
  loading: false
})

const emit = defineEmits(['update:data'])

const formState = shallowRef<Record<string, any>>({})
const loading = ref(props.loading)

const DEFAULT_SPAN = { xs: 24, sm: 24, md: 12, lg: 12, xl: 8, xxl: 6 }

const fieldValueCache = new Map<string, any>()

const getRawFormState = () => formState.value || {}
const getDetail = async () => {
  reset()
  try {
    if (!props.ajaxUrl) {
      return
    }
    loading.value = true
    const res = await props.ajaxUrl(props.params || {})
    props.responseHandle?.(res)
    if (isSuccess(res)) {
      setData(res.data)
    }
  } finally {
    loading.value = false
  }
}

const formatter = (record: IColumnProp, value: string) => {
  if (typeof record.formatter === 'function') {
    return record.formatter(getRawFormState(), getRawFormState()[value])
  }
  return ''
}

const customRender = (cb: IColumnProp['customRender'], value: string) => {
  if (typeof cb === 'function') {
    return cb(getRawFormState(), getRawFormState()[value])
  }
  return null
}

const formatterShow = (val: IColumnProp['show']): boolean => {
  if (typeof val === 'function') {
    return val(getRawFormState())
  }
  return val !== false
}

const getFieldValue = (prop: string | string[]): any => {
  const key = Array.isArray(prop) ? prop.join('.') : prop
  if (fieldValueCache.has(key)) {
    return fieldValueCache.get(key)
  }

  let value: any = getRawFormState()
  if (Array.isArray(prop) && prop.length > 1) {
    for (const k of prop) {
      value = value?.[k]
    }
  } else {
    value = value?.[prop as string]
  }

  fieldValueCache.set(key, value)
  return value
}

const getNestedFieldValue = (path: string[]): any => {
  const key = path.join('.')
  if (fieldValueCache.has(key)) {
    return fieldValueCache.get(key)
  }

  let value: any = getRawFormState()
  for (const k of path) {
    value = value?.[k]
  }

  fieldValueCache.set(key, value)
  return value
}

const clearCache = () => {
  fieldValueCache.clear()
}

const setData = (data: Record<string, any>) => {
  clearCache()
  formState.value = data
  emit('update:data', data)
}

const reset = () => {
  clearCache()
  formState.value = {}
}

defineExpose({
  setData,
  reset,
  formState,
  getDetail
})
</script>

<style lang="less" scoped>
.jt-detail-content {
  :deep(.ant-form-item-label) {
    padding-bottom: 10px;
  }
}

.photo-list {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;

  &-item {
    display: flex;
  }

  :deep(.ant-image) {
    width: 50px;
    height: 50px;
    margin-top: 10px;
    margin-inline-end: 5px;
  }

  :deep(.ant-image-img) {
    border-radius: 6px;
    margin-top: 10px
  }
}

.detail-item {
  :deep(.ant-image-img) {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: #f5f5f5;
  }
}
</style>
